import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, TrendingUp, Moon, Percent, Mail, Check, Info } from "lucide-react";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { getEstimator, createLead } from "../lib/api";
import { estimate, fmtUsd, fmtPct } from "../lib/estimator";
import useReveal from "../hooks/useReveal";

const inputCls = "w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#2f74c9] bg-white";

function Stat({ icon: Icon, label, low, high, hint, testId }) {
  return (
    <div className="bg-white rounded-lg border border-slate-100 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider"><Icon size={14} className="text-[#2f74c9]" /> {label}</div>
      <p className="font-serif-display text-2xl md:text-3xl text-[#0b1f33] mt-2">{low}<span className="text-slate-400 text-lg mx-1">–</span>{high}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Breakdown({ s, label }) {
  const rows = [
    ["Revenu brut", s.gross],
    [`Frais de gestion`, -s.fee],
    ["Frais plateformes", -s.platform],
    [`Ménage & linge (${s.stays} séjours)`, -s.cleaning],
    ["Charges (HOA / entretien)", -s.hoa],
  ];
  return (
    <div className="bg-white rounded-lg border border-slate-100 p-5">
      <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{label} · {s.nights} nuits/an à {fmtUsd(s.adr)}</p>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-t border-slate-100"><td className="py-2 text-slate-600">{k}</td><td className={`py-2 text-right ${v < 0 ? "text-rose-600" : "text-[#0b1f33]"}`}>{v < 0 ? "− " : ""}{fmtUsd(Math.abs(v))}</td></tr>
          ))}
          <tr className="border-t-2 border-[#0b1f33]"><td className="py-2 font-600 text-[#0b1f33]">Revenu net annuel</td><td className="py-2 text-right font-600 text-[#0b1f33]">{fmtUsd(s.net)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

export default function Estimator() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ zone: "pdc_centro", bedrooms: "1", hoaMonthly: "", price: "" });
  const [lead, setLead] = useState({ name: "", email: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  useReveal([settings]);
  useEffect(() => { getEstimator().then(setSettings).catch(() => setSettings(null)); }, []);

  const result = useMemo(() => (settings ? estimate(settings, form) : null), [settings, form]);
  const zoneLabel = settings?.zones.find((z) => z.key === form.zone)?.label || "";
  const bdLabel = settings?.bedroom_labels[form.bedrooms] || "";

  const sendStudy = async (e) => {
    e.preventDefault();
    if (!result) return;
    setSending(true);
    const { low, high } = result;
    const message = [
      `Estimation de revenus locatifs — ${zoneLabel}, ${bdLabel}`,
      form.price ? `Prix d'achat : ${fmtUsd(form.price)}` : null,
      form.hoaMonthly ? `Charges mensuelles : ${fmtUsd(form.hoaMonthly)}` : null,
      `Tarif/nuit : ${fmtUsd(low.adr)} – ${fmtUsd(high.adr)} · Occupation : ${fmtPct(low.occ)} – ${fmtPct(high.occ)}`,
      `Revenu brut annuel : ${fmtUsd(low.gross)} – ${fmtUsd(high.gross)}`,
      `Revenu net annuel : ${fmtUsd(low.net)} – ${fmtUsd(high.net)} (${fmtUsd(low.netMonthly)} – ${fmtUsd(high.netMonthly)} / mois)`,
      low.roi != null ? `Rendement net : ${fmtPct(low.roi)} – ${fmtPct(high.roi)}` : null,
      `Hypothèses : gestion ${settings.fee_pct} %, plateformes ${settings.platform_pct} %, séjour moyen ${settings.avg_stay_nights} nuits. ${settings.sources}`,
    ].filter(Boolean).join("\n");
    try {
      await createLead({ name: lead.name, email: lead.email, phone: "", subject: "Estimation de revenus", message, website: "" });
      setSent(true);
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.status === 429 ? "Trop de demandes. Réessayez plus tard." : "L'envoi a échoué, veuillez réessayer." });
    }
    setSending(false);
  };

  return (
    <div>
      <Toaster />
      <section className="relative pt-40 pb-20 bg-[#0b1f33] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1512917774080-9991f1c4c750)" }} />
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <p className="text-[#2f74c9] tracking-[0.3em] text-xs uppercase mb-4">Location courte durée</p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-white">Estimez vos revenus locatifs</h1>
          <p className="text-white/70 mt-5 max-w-2xl mx-auto">Des chiffres réels de la Riviera Maya, actualisés : tarif par nuit, taux d'occupation, revenu net et rendement de votre propriété en gestion clé en main.</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="reveal bg-white rounded-lg p-7 border border-slate-100 shadow-sm h-fit" data-testid="estimator-form">
            <div className="flex items-center gap-2 mb-6"><Calculator size={18} className="text-[#2f74c9]" /><h2 className="font-serif-display text-2xl text-[#0b1f33]">Votre propriété</h2></div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Secteur</label>
            <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="estimator-zone" className={inputCls}>
              {(settings?.zones || []).map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
            </select>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5 mt-5">Chambres</label>
            <select value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} data-testid="estimator-bedrooms" className={inputCls}>
              {Object.entries(settings?.bedroom_labels || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5 mt-5">Prix d'achat (USD, optionnel)</label>
            <input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="estimator-price" placeholder="ex. 185 000" className={inputCls} />
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5 mt-5">Charges mensuelles HOA / entretien (USD, optionnel)</label>
            <input type="number" min="0" value={form.hoaMonthly} onChange={(e) => setForm({ ...form, hoaMonthly: e.target.value })} data-testid="estimator-hoa" placeholder="ex. 150" className={inputCls} />
            <p className="text-xs text-slate-400 mt-6 flex gap-2"><Info size={14} className="shrink-0 mt-0.5" /> Fourchette basse = moyenne du marché ; fourchette haute = propriété optimisée en gestion professionnelle. {settings?.sources}</p>
          </div>

          <div className="lg:col-span-2 space-y-6">
            {!settings ? (
              <p className="text-slate-400 py-16 text-center">Chargement du simulateur…</p>
            ) : !result ? (
              <p className="text-slate-400 py-16 text-center">Aucune donnée pour cette combinaison.</p>
            ) : (
              <>
                <div className="reveal grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Stat icon={Moon} label="Tarif par nuit" low={fmtUsd(result.low.adr)} high={fmtUsd(result.high.adr)} testId="estimator-adr" />
                  <Stat icon={Percent} label="Taux d'occupation" low={fmtPct(result.low.occ)} high={fmtPct(result.high.occ)} hint={`${result.low.nights} – ${result.high.nights} nuits louées par an`} testId="estimator-occ" />
                  <Stat icon={TrendingUp} label="Revenu net mensuel" low={fmtUsd(result.low.netMonthly)} high={fmtUsd(result.high.netMonthly)} hint="après gestion, plateformes, ménage et charges" testId="estimator-net-monthly" />
                  <Stat icon={TrendingUp} label={form.price ? "Rendement net annuel" : "Revenu net annuel"} low={form.price ? fmtPct(result.low.roi) : fmtUsd(result.low.net)} high={form.price ? fmtPct(result.high.roi) : fmtUsd(result.high.net)} hint={form.price ? `${fmtUsd(result.low.net)} – ${fmtUsd(result.high.net)} / an` : "renseignez un prix d'achat pour le rendement"} testId="estimator-net-annual" />
                </div>
                <div className="reveal reveal-delay-1 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Breakdown s={result.low} label="Scénario marché" />
                  <Breakdown s={result.high} label="Scénario optimisé Bella Edifices" />
                </div>
                <div className="reveal reveal-delay-2 bg-[#0b1f33] rounded-lg p-7 text-white" data-testid="estimator-lead-box">
                  {sent ? (
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0"><Check size={22} className="text-emerald-400" /></div>
                      <div><p className="font-serif-display text-2xl">Étude envoyée</p><p className="text-white/70 text-sm mt-1">Un gestionnaire Bella Edifices vous recontacte sous 24 h avec une analyse personnalisée.</p></div>
                    </div>
                  ) : (
                    <form onSubmit={sendStudy} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                      <div className="md:col-span-3 mb-1"><p className="font-serif-display text-2xl">Recevoir l'étude détaillée</p><p className="text-white/70 text-sm mt-1">Nous vous envoyons cette estimation et un gestionnaire affine les chiffres pour votre bien.</p></div>
                      <input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Votre nom" data-testid="estimator-lead-name" className="rounded-md bg-white/10 border border-white/20 px-4 py-3 text-sm outline-none focus:border-[#2f74c9] placeholder:text-white/50" />
                      <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Votre courriel" data-testid="estimator-lead-email" className="rounded-md bg-white/10 border border-white/20 px-4 py-3 text-sm outline-none focus:border-[#2f74c9] placeholder:text-white/50" />
                      <button type="submit" disabled={sending} data-testid="estimator-lead-submit" className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-600 text-sm disabled:opacity-60"><Mail size={15} /> {sending ? "Envoi…" : "Envoyer"}</button>
                    </form>
                  )}
                  <p className="text-white/50 text-xs mt-5">Ou <Link to="/contact" className="underline hover:text-white">parlez directement à un gestionnaire</Link>. Estimation indicative, non contractuelle.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
